import React from "react";
import { Box, Modal, Button, Typography } from "@mui/material";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import RenderBadges from "./RenderBadges";

const ModalUpp = ({
  open,
  onClose,
  dyzData,
  partsData,
  subPartsData,
  usesCharData,
  usesMCWData,
  phrasesData,
  paronymData,
  audioPlaying,
  handlePlayAudio,
  hanziId,
  type,
  character,
  ptext,
  pinyin,
  measure,
  dyz,
  hskLevel,
  hskComp,
  hskFreq,
  speech,
  proper,
  duoSection,
  duoUnit,
  domChapter,
  domCourse,
  domLesson,
  speechPart3,
  uncommon,
}) => {
  const renderParts = (data, showBUpp = true) => {
    if (!data) return null;
    const { hanzi_character, hanzi_pinyin, hanzi_meaning, hanzi_type } = data;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
        <Typography variant="body1" sx={{ fontSize: "22px", fontWeight: 300, lineHeight: 1.1 }}>
          {hanzi_character.replace(/\s+/g, "")}
        </Typography>
        <Typography
          variant="body1"
          onClick={audioPlaying ? null : () => handlePlayAudio(hanzi_character, hanzi_type)}
          sx={{ color: "primary.main", fontFamily: "Spectral", cursor: "pointer", lineHeight: 1.2 }}
        >
          {hanzi_pinyin}
        </Typography>
        <Typography variant="body1" sx={{ fontSize: "14px", lineHeight: 1.2 }}>
          {hanzi_meaning}
        </Typography>
        <RenderBadges data={data} showBUpp={showBUpp} />
      </Box>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: "75%", md: 600 },
          maxWidth: "600px",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: { xs: 1, sm: 3 },
          borderRadius: 2,
          maxHeight: "80vh",
          overflowY: "auto",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Building Blocks, Uses, Phrases & Paronyms
        </Typography>
        {/* Measure Accordion */}
        {measure && measure.length > 0 && (
          <Accordion sx={{ mb: 2, bgcolor: (theme) => theme.palette.darkPaper }} defaultExpanded>
            <AccordionSummary sx={{ mt: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                  Measure
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ py: 0 }}>
              <Typography variant="body1" sx={{ textAlign: "left", ml: 2, mb: 1 }}>
                {measure.charAt(0).toUpperCase() + measure.slice(1)}
              </Typography>
            </AccordionDetails>
          </Accordion>
        )}
        {/* DYZ Accordion (characters with >1 pronunciations) */}
        {dyzData.length > 0 && (
          <Accordion sx={{ mb: 2, bgcolor: (theme) => theme.palette.darkPaper }} defaultExpanded>
            <AccordionSummary sx={{ mt: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                  多音字 - Multiple Pronunciations
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ py: 0 }}>
              {dyzData.map((use, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  {renderParts(use, false)}
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        )}
        <Accordion sx={{ pb: 4 }} defaultExpanded>
          <AccordionSummary sx={{ mt: 0 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
              <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                Building Blocks
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                {partsData.length}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ py: 0, my: -2 }}>
            {partsData.map((part, index) =>
              subPartsData[index]?.length > 0 ? ( // Only render the Accordion if there are subparts
                <Accordion key={index} defaultExpanded sx={{ boxShadow: "none" }}>
                  <AccordionSummary sx={{ mt: -2, mb: 0, px: 0 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                      {renderParts(part, false)}
                      {/* <Typography variant="body2" sx={{ textAlign: "right" }}>
                                {subPartsData[index]?.length || 0}
                              </Typography> */}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ py: 0, my: -2, mb: 0 }}>
                    {subPartsData[index].map((subPart, subIndex) => (
                      <Box key={subIndex}>{renderParts(subPart, false)}</Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Box key={index}>
                  <AccordionSummary sx={{ mt: -1, mb: 0, px: 0 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                      {renderParts(part, false)}
                      {/* <Typography variant="body2" sx={{ textAlign: "right" }}>
                                0
                              </Typography> */}
                    </Box>
                  </AccordionSummary>
                </Box>
              )
            )}
          </AccordionDetails>
        </Accordion>
        {/* Uses Char Accordion */}
        {type < 3 ? (
          Array.isArray(usesCharData) && usesCharData.length > 0 ? (
            <Accordion sx={{ mt: { xs: 2, md: 0 }, mb: 2 }}>
              <AccordionSummary sx={{ mt: 0 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                    Uses: Char
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                    {usesCharData.length}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ py: 0 }}>
                {usesCharData.slice(0, 5).map((use, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    {renderParts(use, false)}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ) : (
            <Accordion sx={{ my: { xs: 2, md: 0 } }}>
              <AccordionSummary sx={{ mt: 0 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", my: { xs: 2, md: 0 } }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                    Uses: Char
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                    0
                  </Typography>
                </Box>
              </AccordionSummary>
            </Accordion>
          )
        ) : (
          // For multi-character records, display separate accordion for each character
          (() => {
            if (!character) return null;
            const characters =
              type === 1
                ? [character.split(" ")[0].charAt(0)] // Type 1: use first character of first word
                : character.split(""); // Type 3,4: use all characters
            return characters.map((char, charIndex) => {
              const charData = usesCharData[char] || [];
              return charData.length > 0 ? (
                <Accordion key={charIndex} sx={{ mt: charIndex === 0 ? { xs: 2, md: 0 } : 0, mb: 0 }}>
                  <AccordionSummary sx={{ mt: 0 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                        Uses Char: {char}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                        {charData.length}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ py: 0 }}>
                    {charData.slice(0, 5).map((use, index) => (
                      <Box key={index} sx={{ mb: 2 }}>
                        {renderParts(use, false)}
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Accordion key={charIndex} sx={{ mt: charIndex === 0 ? { xs: 2, md: 0 } : 0, mb: 0 }}>
                  <AccordionSummary sx={{ mt: 0 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                        Uses Char: {char}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                        0
                      </Typography>
                    </Box>
                  </AccordionSummary>
                </Accordion>
              );
            });
          })()
        )}

        {/* Uses MCW Accordion */}
        {usesMCWData.length > 0 && type < 3 ? (
          <Accordion sx={{ my: { xs: 2, md: 0 } }}>
            <AccordionSummary sx={{ mt: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                  Uses: MCW
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                  {usesMCWData.length}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ py: 0 }}>
              {usesMCWData.slice(0, 5).map((use, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  {renderParts(use, false)}
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        ) : usesMCWData.length > 0 && type > 2 && character ? (
          character.split("").map((char, charIndex) => {
            const charRecords = usesMCWData.filter((item) => item.hanzi_character.includes(char));
            return charRecords.length > 0 ? (
              <Accordion key={charIndex} sx={{ mt: charIndex === 0 ? 2 : 0 }}>
                <AccordionSummary sx={{ mt: 0 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                      Uses MCW: {char}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                      {charRecords.length}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ py: 0 }}>
                  {charRecords.slice(0, 5).map((use, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      {renderParts(use, false)}
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            ) : null;
          })
        ) : (
          <Accordion sx={{ my: 0 }}>
            <AccordionSummary sx={{ mt: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", my: { xs: 2, md: 0 } }}>
                <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                  Uses MCW
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                  0
                </Typography>
              </Box>
            </AccordionSummary>
          </Accordion>
        )}

        {/* Phrases Accordion */}
        {phrasesData.length > 0 && type < 4 ? (
          <Accordion sx={{ my: 2 }}>
            <AccordionSummary sx={{ mt: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                  Phrases
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                  {phrasesData.length}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ py: 0 }}>
              {phrasesData.slice(0, 5).map((phrase, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  {renderParts(phrase, false)}
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        ) : type < 4 ? (
          <Accordion sx={{ my: 2 }}>
            <AccordionSummary sx={{ mt: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                  Phrases
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                  0
                </Typography>
              </Box>
            </AccordionSummary>
          </Accordion>
        ) : null}

        {/* Paronym Accordion */}
        {paronymData.length > 0 && type < 3 ? (
          <Accordion sx={{ my: 2 }}>
            <AccordionSummary sx={{ mt: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                  Paronyms
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                  {paronymData.length}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ py: 0 }}>
              {paronymData.map((paronym, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  {renderParts(paronym, false)}
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        ) : paronymData.length > 0 && type > 2 ? (
          (() => {
            const characters = character.split("");
            const pinyins = pinyin.split(" ");
            const ptexts = ptext.split(" ");

            return characters.map((char, charIndex) => {
              const currentPtext = ptexts[charIndex]?.replace(/\d+$/, "") || "";
              const matchingRecords = paronymData.filter(
                (item) => item.hanzi_ptext.replace(/\d+$/, "") === currentPtext
              );

              return matchingRecords.length > 0 ? (
                <Accordion key={charIndex} sx={{ mt: charIndex === 0 ? 2 : 0 }}>
                  <AccordionSummary sx={{ mt: 0 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                        Paronyms: {char} {pinyins[charIndex] || ""}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                        {matchingRecords.length}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ py: 0 }}>
                    {matchingRecords.map((paronym, index) => (
                      <Box key={index} sx={{ mb: 2 }}>
                        {renderParts(paronym, false)}
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ) : null;
            });
          })()
        ) : (
          <Accordion sx={{ my: 2 }}>
            <AccordionSummary sx={{ mt: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                  Paronyms
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                  0
                </Typography>
              </Box>
            </AccordionSummary>
          </Accordion>
        )}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ModalUpp;
